import type { Chart, ChartData, ChartOptions } from "chart.js";

import type { OpenAPI } from "simplydo/interfaces";
import type { UserMe } from "simplydo/interfaces/types/userTypes";
import type { SidebarIconName } from "simplydo/react/components/SidebarIcons";
import type { DropdownOption } from "simplydo/react/ui/dropdown";
import { IconData } from "simplydo/react/ui/icon";

export type { UserMe };

/** Organisation-scoped admin context from the store. */
export type AdminOrgId = string;

export type AdminOrganisationUser =
  OpenAPI.GET<"/organisations/{id}/users">["response"]["users"][number];

export type AdminOrganisationAdmin =
  OpenAPI.GET<"/organisations/{id}/admins">["response"]["users"][number];

export type ShortenedLink =
  OpenAPI.GET<"/organisations/{id}/shortenedUrls">["response"]["shortenedUrls"][number];

export type ShortenedLinkDetail =
  OpenAPI.GET<"/organisations/{id}/shortenedUrls/{url_id}">["response"];

export type OrganisationRule = OpenAPI.Schemas["Rule"];

export type OrganisationRuleListItem =
  OpenAPI.GET<"/organisations/{id}/rules">["response"][number];

export type OrganisationGroupListItem =
  OpenAPI.GET<"/organisations/{id}/groups">["response"]["groups"][number] & {
    _id: string;
  };

export type OrganisationTag = OpenAPI.Schemas["Tag"];

export type OrganisationGoal =
  OpenAPI.GET<"/organisations/{id}/goals">["response"]["goals"][number];

export type OrganisationImpact =
  OpenAPI.GET<"/organisations/{id}/impacts">["response"]["impacts"][number];

export type OrganisationOverviewStats = Omit<
  OpenAPI.GET<"/organisations/{id}/overview">["response"],
  | "usersMonthlySignups"
  | "usersMonthlySignupsCumulative"
  | "activeUsersMonthly"
  | "ideasCreatedMonthly"
  | "ideasMonthlyCumulative"
  | "ideasSubmittedMonthly"
> & {
  engagementGoals?: OverviewEngagementGoal[];
  usersMonthlySignups?: OverviewMonthlyData;
  usersMonthlySignupsCumulative?: OverviewMonthlyData;
  activeUsersMonthly?: OverviewMonthlyData;
  ideasCreatedMonthly?: OverviewMonthlyData;
  ideasMonthlyCumulative?: OverviewMonthlyData;
  ideasSubmittedMonthly?: OverviewMonthlyData;
};

export type OrganisationChallengeOverviewTagFilter = {
  _id: string;
  value: string;
};

export type OrganisationChallengesOverview =
  OpenAPI.GET<"/organisations/{id}/overview/challenges">["response"] & {
    tagFilters?: OrganisationChallengeOverviewTagFilter[];
    challengeStageFilters?: NonNullable<
      OpenAPI.GET<"/organisations/{id}/overview/challenges">["response"]["challengeStageFilter"]
    >;
  };

export type OrganisationDashboardListItem =
  OpenAPI.Schemas["organisationDashboards"];

export type OrganisationDashboard = Omit<
  OpenAPI.Schemas["organisationDashboards"],
  "segments"
> & { segments?: OrganisationDashboardSegment[] };

export type OrganisationDashboardSegment = NonNullable<
  OpenAPI.Schemas["organisationDashboards"]["segments"]
>[number] &
  NonNullable<
    NonNullable<OpenAPI.Schemas["organisationSegments"]["segments"]>[number]
  > & {
    key?: string;
    editing?: boolean;
    type?: string;
    measure?: string;
    data?: {
      type?: "line" | "bar" | string;
      domain?: string[];
      sourceNames?: string[];
      ranges?: Record<string, number | number[]>;
    };
  };

export type OrganisationEmailLog =
  OpenAPI.GET<"/organisations/{org_id}/communication/emails/logs">["response"]["logs"][number];

export type OrganisationEmailLogDetail =
  OpenAPI.GET<"/organisations/{org_id}/communication/emails/logs/{log_id}">["response"]["log"];

export type OrganisationUpload = OpenAPI.Schemas["AdminUploads"] & {
  url?: string;
};

export type OrganisationBusinessProfile =
  OpenAPI.GET<"/organisations/{org_id}/profiles">["response"]["profiles"][number];

export type Dynamics365Customer =
  OpenAPI.GET<"/organisations/{id}/dynamics365/customers">["response"]["customers"][number];

export type AdminChallengeListItem =
  OpenAPI.GET<"/organisations/{id}/challenges">["response"]["challenges"][number] & {
    stage?: "published" | "closed" | "draft";
  };

export type AdminExploreIdea =
  OpenAPI.GET<"/organisations/{id}/explore/ideas">["response"]["ideas"][number];

export type AdminExploreOptions =
  OpenAPI.GET<"/organisations/{id}/explore/ideas/options">["response"];

export type AdminExploreChallenge = AdminExploreOptions["challenges"][number];

export type CommunicationTemplate = OpenAPI.Schemas["emailTemplates"][string];

export type EmailTemplatesMap = OpenAPI.Schemas["emailTemplates"];

export type EmailTemplateEventKey =
  | "closingSoon"
  | "stageChanged"
  | "discussionPosted"
  | "groupInvited"
  | "groupJoinRequested"
  | "ideaCommented"
  | "ideaVoted"
  | "ideaInvited"
  | "messaged"
  | "collaboratorAdded"
  | "mentionedIdeaComment"
  | "unlockedAchievement"
  | "ideaSubmitted"
  | "projectAssignedAssignee"
  | "projectAssignedManager"
  | "projectInvited"
  | "groupJoinAccepted"
  | "groupJoinRejected"
  | "groupInviteRejected"
  | "assessorAssigned";

export type EmailTemplateEditorState = {
  name: EmailTemplateEventKey;
  template: CommunicationTemplate;
} | null;

export type AdminExploreResults = {
  ideas: AdminExploreIdea[];
  loading: boolean;
  total: number;
  previousPageAvailable: boolean;
  nextPageAvailable: boolean;
  availableSortParameters: { sliders?: AdminExploreSortParameter[] };
  availableFilterParameters: { fields?: AdminExploreFilterParameter[] };
};

export type OrganisationActivityRequest =
  OpenAPI.POST<"/organisations/{id}/activity">["requestBody"] & {
    ideaFilter?: string;
    challFilter?: string;
    recentFilter?: string;
  };

type PreparedUserRef = { _id: string; profile: { fullName: string } };

export type AdminRecentIdea = OpenAPI.Schemas["Idea"] & {
  challenge: { _id: string; name: string };
  user: PreparedUserRef;
};

export type AdminEngagedIdea = {
  _id: string;
  count: number;
  idea: { _id: string; name: string };
  challenge: { _id: string; name: string };
  user: PreparedUserRef;
};

export type AdminEngagedChallenge = {
  _id: string;
  challenge: string;
  count: number;
};

export type OrganisationActivityResponse = Omit<
  OpenAPI.POST<"/organisations/{id}/activity">["response"],
  "recent_ideas" | "engaged_ideas" | "engaged_challs"
> & {
  recent_ideas: AdminRecentIdea[];
  engaged_ideas: AdminEngagedIdea[];
  engaged_challs: AdminEngagedChallenge[];
};

export type OrganisationActivityOverviewResponse = {
  activity: OrganisationActivityOverviewCard;
};

export type OrganisationActivityOverviewCard = {
  users: number;
  activePercentage: number;
  active: number;
  totalUsers: number;
  created: number;
  submitted: number;
  likes: number;
  comments: number;
};

export type OrganisationImpactMeasure =
  OpenAPI.GET<"/organisations/{id}/impact">["response"]["impactMeasures"][number];

export type AdminImpactIdea =
  OpenAPI.GET<"/organisations/{id}/impact">["response"]["ideas"][number] & {
    authors?: PreparedUserRef[];
    impacts?: {
      impactId?: string;
      value?: string | number;
      ownerImpact?: { _id: string; name: string; icon?: string };
    }[];
  };

export type AdminExploreSearchParams = {
  page: number;
  limit: number;
  challenges: string[];
  departments: string[];
  tags: string[];
  status: "" | "draft" | "submitted";
  name: string;
  progression: string[];
  labels: string[];
  creationDate: string;
  sort: string;
  sortDirection: "asc" | "desc";
  fieldChoices: string[];
  laneSearchName: string;
  submissionStartDate?: string;
  submissionEndDate?: string;
};

export type EmailTemplateEventMeta = { title: string; placeholders: string[] };

export const defaultAdminExploreSearchParams: AdminExploreSearchParams = {
  departments: [],
  challenges: [],
  tags: [],
  status: "",
  name: "",
  progression: [],
  labels: [],
  creationDate: "",
  page: 1,
  limit: 50,
  sort: "submittedAt",
  sortDirection: "desc",
  fieldChoices: [],
  laneSearchName: "",
};

export type AdminExploreSortParameter = {
  type: string;
  name: string;
  _id: string;
  value: string;
};

export type AdminExploreFilterParameter = {
  _id: string;
  type: string;
  name: string;
  description?: string;
  options?: string[];
};

export type OverviewEngagementGoal = OrganisationGoal & {
  count?: number;
  dailyCount?: number;
  isHittingTarget?: boolean;
  monthIndex?: number;
};

export type OverviewMonthlyData = Record<string, Record<string, number>>;

export type EditRule = {
  _id?: string;
  name: string;
  order: string;
  targetConditionType?: OrganisationRule["targetConditionType"];
  targets: RuleEditTarget[];
  actions: { id: string; type: string; typeId: string }[];
};

export type RuleEditAction = EditRule["actions"][number];

export type RuleEditTarget = {
  id: string;
  type?: string;
  attributeName?: string;
  attributePattern?: string;
  groupId?: string;
  urlId?: string;
};

export type RuleApiPayload =
  OpenAPI.POST<"/organisations/{id}/rules">["requestBody"];

export type LineChartData = ChartData<"line", number[], string>;

export type LineChartOptions = ChartOptions<"line">;

export type BarChartData = ChartData<"bar", number[], string>;

export type BarChartOptions = ChartOptions<"bar">;

export type LinkChartDataFn = (link: ShortenedLink) => LineChartData | null;

export type PieChartLegendGenerator = (
  chart: Chart<"pie">,
) => {
  text: string;
  fillStyle: string;
  hidden: boolean;
  index: number;
  strokeStyle: string;
  lineWidth: number;
}[];

export type ShortenedUrlSearchResults =
  OpenAPI.GET<"/organisations/{id}/shortenedUrls/search">["response"]["searchResults"];

export type ShortenedUrlSearchItem =
  ShortenedUrlSearchResults["challenges"][number];

export type ShortenedUrlClicksMap = Record<string, Record<string, number>>;

export type OrganisationBusinessSearchState = NonNullable<
  OpenAPI.GET<"/organisations/{id}/businesses">["parameters"]["query"]
>;

export type CreditsafeBusinessListItem =
  OpenAPI.GET<"/organisations/{id}/businesses">["response"]["creditsafeBusinesses"][number];

export type OrganisationBusinessListProfile =
  OpenAPI.GET<"/organisations/{id}/businesses">["response"]["businessProfiles"][number];

export type AdminListUser =
  OpenAPI.GET<"/organisations/{id}/admins">["response"]["users"][number];

export type CreateUserItem = {
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  jobTitle: string;
  include?: boolean;
  success?: boolean;
  errorMessage?: string;
};

export type UserSortKey = keyof AdminOrganisationUser;

export type UserFilter = {
  userPage: number;
  userQuery: string;
  userSort: UserSortKey | "";
  userSortDirection: "asc" | "desc" | undefined;
  userView: string;
  userDepartment: string;
};

export type LinksSearchState = {
  startDay: number;
  endDay: number;
  query: string;
  sort: ShortenedUrlsSort | "";
  limit: number;
  page: number;
  groupByUrl: boolean;
};

export type ShortenedUrlsQueryParams = NonNullable<
  OpenAPI.GET<"/organisations/{id}/shortenedUrls">["parameters"]["query"]
>;

export type ShortenedUrlsSort = NonNullable<ShortenedUrlsQueryParams["sort"]>;

export type ShortenedUrlsListResponse = Omit<
  OpenAPI.GET<"/organisations/{id}/shortenedUrls">["response"],
  "clicks"
> & { clicks: ShortenedUrlClicksMap };

export const toShortenedUrlsQuery = (
  state: LinksSearchState,
): ShortenedUrlsQueryParams => ({
  startDay: state.startDay,
  endDay: state.endDay,
  groupByUrl: state.groupByUrl,
  page: state.page,
  limit: state.limit,
  ...(state.sort ? { sort: state.sort } : {}),
});

export type LinkSortKey = "path" | "createdAt" | "clicks";

export type ClickDateKey =
  | "last30days"
  | "last60days"
  | "last90days"
  | "custom";

export type LinksCustomDates = { startDay: Date; endDay: Date };

export const overviewChartOptions: LineChartOptions = {
  layout: { padding: { right: 8 } },
  maintainAspectRatio: false,
  scales: {
    y: { beginAtZero: true, grid: { color: "rgba(0,0,0,0.03)" } },
    x: { grid: { display: false } },
  },
  plugins: { legend: { display: false } },
};

export const adminLineChartOptions = (): LineChartOptions => ({
  layout: { padding: { right: 8 } },
  maintainAspectRatio: false,
  scales: {
    y: { beginAtZero: true, grid: { color: "rgba(0,0,0,0.03)" } },
    x: { grid: { color: "rgba(0,0,0,0)" } },
  },
});

export const adminBarChartOptions = (): BarChartOptions => ({
  layout: { padding: { right: 8 } },
  maintainAspectRatio: false,
  scales: {
    y: { beginAtZero: true, grid: { color: "rgba(0,0,0,0.03)" } },
    x: { grid: { color: "rgba(0,0,0,0)" } },
  },
  plugins: { legend: { display: false } },
});

export function unwrappedApiList<T>(data: unknown, listKey: string): T[] {
  if (Array.isArray(data)) {
    return data as T[];
  }
  if (data && typeof data === "object" && listKey in data) {
    const list = (data as Record<string, unknown>)[listKey];
    return Array.isArray(list) ? (list as T[]) : [];
  }
  return [];
}

export function dropdownStringValue(
  value: string | number | boolean | (string | number | boolean)[] | undefined,
): string | undefined {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return undefined;
}

export function dropdownStringArrayValue(
  value: string | number | boolean | (string | number | boolean)[] | undefined,
): string[] {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry));
  }
  if (value === undefined || value === "") {
    return [];
  }
  return [String(value)];
}

export function dropdownNumberValue(
  value: string | number | boolean | (string | number | boolean)[] | undefined,
): number | undefined {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value !== "") return Number(value);
  return undefined;
}

export function asDropdownOptions(
  options: readonly DropdownOption[],
): DropdownOption[] {
  return [...options];
}

export function organisationActivityRequest(
  params: Omit<OrganisationActivityRequest, "challengeIds"> & {
    challengeIds?: string[];
  },
): OpenAPI.POST<"/organisations/{id}/activity">["requestBody"] {
  return {
    challengeIds: params.challengeIds ?? [],
    freq: params.freq,
    ideaFilter: params.ideaFilter,
    challFilter: params.challFilter,
    recentFilter: params.recentFilter,
    userIds: params.userIds,
    ideaIds: params.ideaIds,
  } as OpenAPI.POST<"/organisations/{id}/activity">["requestBody"];
}

/** Redux helper for optimistic organisation field updates in settings screens. */
export type OnOrganisationEditedFn = (field: string, value: unknown) => void;

export type OrganisationTagListItem = OpenAPI.Schemas["tags"] &
  OpenAPI.Schemas["tagIdeasCount"] &
  OpenAPI.Schemas["tagChallengesCount"] &
  OpenAPI.Schemas["tagUsers"] &
  OpenAPI.Schemas["tagTotalCount"] & {
    ideaBusinessProfilesCount?: number;
    usersCount?: number;
  };

export type OrganisationTagList = OrganisationTagListItem[];

export type TagDisabledEntity = NonNullable<
  OrganisationTagListItem["disabledEntities"]
>[number];

export type TagUpdatePayload = {
  value?: string;
  description?: string;
  disabledEntities?: TagDisabledEntity[];
};

export type EditingTag = OrganisationTagListItem & { changed?: boolean };

export type TagSort = "nameAsc" | "nameDesc" | "usesAsc" | "usesDesc";

export type GoalEntity = "users" | "ideas";

export type PointOfContactRole =
  OpenAPI.GET<"/organisations/{org_id}/contact/roles">["response"]["pointOfContactRoles"][number] & {
    ownerAssignedTo?: OpenAPI.Schemas["users"];
  };

export type SmtpServerFormState = {
  isEnabled?: boolean;
  serverHost: string;
  serverPort: number;
  serverSsl: boolean | string | null;
  name: string;
  emailAddress: string;
  serverUsername: string;
  serverPassword: string;
};

export type IpAllowListEntry = { address: string; description?: string };

export type OrgNavLink = NonNullable<
  OpenAPI.Schemas["Organisation"]["links"]
>[number];

export type OrgNavBarItem = NonNullable<
  OpenAPI.Schemas["Organisation"]["navbar"]
>[number] & {
  translate?: string;
  title?: string;
  location?: string;
  external?: boolean;
  permissions?: string[];
  icon?: IconData;
};

export type AdminSidebarItem = Omit<
  NonNullable<OpenAPI.Schemas["Organisation"]["adminSidebar"]>[number],
  "icon"
> & {
  translate?: string;
  title?: string;
  location?: string;
  external?: boolean;
  permissions?: string[];
  icon?: IconData;
  iconName?: SidebarIconName;
  // Pattern passed to util.activePath to highlight the entry
  activePath?: string;
  // Set for custom pages, which are listed to managers regardless of access
  canView?: boolean;
  // Entries that would leave an admin unable to reach these settings again
  required?: boolean;
  visibleUserCount?: number;
  visibleRoleNames?: string[];
};

export type MappedImpactCategory = Partial<
  OpenAPI.Schemas["OrganisationImpactCategory"]
> & {
  name: string;
  order: number;
  createdAt: string;
  organisation: string;
  impacts?: OrganisationImpact[];
  collapsed?: boolean;
};

export type OrganisationProductTour = OpenAPI.Schemas["productTours-items"];

export type ProductTourId = NonNullable<OrganisationProductTour["id"]>;

export type SettingsOwnerOrganisation = NonNullable<
  UserMe["ownerOrganisation"]
> & {
  tagList?: OrganisationTagList;
  mailConfiguration?: "default" | "custom" | "outlook";
  terminology?: OrganisationTerminology;
  enabledFeatures?: Array<
    | NonNullable<
        NonNullable<UserMe["ownerOrganisation"]>["enabledFeatures"]
      >[number]
    | "customOrganisationFont"
  >;
};

export type OrganisationTerminology = Record<
  string,
  {
    challenge?: string;
    challenges?: string;
    idea?: string;
    ideas?: string;
    impact?: string;
    impacts?: string;
  }
>;

export type OrganisationSettingsUpdate =
  OpenAPI.PUT<"/organisations/{id}/settings">["requestBody"] & {
    links?: OrgNavLink[];
    navbar?: { id: string; type?: string }[] | null;
    terminology?: OrganisationTerminology;
    mailConfiguration?: SettingsOwnerOrganisation["mailConfiguration"];
    bannerImage?: string;
    customTerms?: { enabled?: boolean; terms?: string };
  };

export type OrganisationFontFamily = NonNullable<
  SettingsOwnerOrganisation["fontFamily"]
>;

export type AuthType =
  OpenAPI.GET<"/organisations/{id}/auth/types">["response"]["types"][number];

export type OrganisationImpactCategory =
  OpenAPI.Schemas["OrganisationImpactCategory"];

export type EditingImpact = Partial<OrganisationImpact> & { name: string };

export type ImpactListResponse =
  OpenAPI.GET<"/organisations/{id}/impacts">["response"];

/** Runtime impact mutation payloads include full lists beyond OpenAPI single-impact responses. */
export type ImpactMutationResponse = ImpactListResponse & {
  impact?: OrganisationImpact;
};

export type ValidateIpActionRequest =
  OpenAPI.PUT<"/organisations/{id}/settings/validateIpAction">["requestBody"] & {
    ip?: IpAllowListEntry | null;
  };

export type SubscriptionMethod = "email" | "push";

export type Dynamics365Connection = {
  isEnabled?: boolean;
  settings?: {
    company?: {
      id?: string;
      name?: string;
      displayName?: string;
      ideaBusinessProfile?: string;
      systemVersion?: string;
    } | null;
  };
};

export type Dynamics365CompaniesResponse =
  OpenAPI.GET<"/organisations/{id}/dynamics365/customers">["response"] & {
    companies?: Array<{ id?: string; name?: string }>;
  };

export type PowerbiDataset = {
  type?: string;
  dataset?: string;
  workspaceId?: string;
  workspaceName?: string;
  lastSyncedAt?: string;
};

export type PowerbiConnection = {
  connectedUser?: OpenAPI.Schemas["PreparedUser"];
  datasets?: PowerbiDataset[];
};

export type MicrosoftMappings = {
  firstName?: string;
  lastName?: string;
  email?: string;
  jobTitle?: string;
  department?: string;
};

export type MicrosoftCustomMapping = {
  localKey: string;
  foreignKey: string;
  id: string;
};

export type MicrosoftSettings =
  OpenAPI.GET<"/organisations/{id}/auth/microsoft">["response"] & {
    groups?: string[];
    mappings?: MicrosoftMappings;
    customMappings?: Record<string, string>;
  };

export type AdConnection = {
  customQueryParameters?: string;
  enabledServices?: string[];
  dataSource?: string;
  targetGroups?: string[];
  scope?: string;
  availableGroups?: Array<{ id?: string; name?: string; description?: string }>;
  activeDirectorySync?: {
    lastSyncedAt?: string;
    error?: string;
    syncs?: Array<{
      createdAt?: string;
      usersCreated?: unknown[];
      usersUpdated?: unknown[];
    }>;
  } | null;
  activeDirectorySyncs?: AdConnection["activeDirectorySync"];
  recentSyncs?: Array<{
    createdAt?: string;
    usersCreated?: unknown[];
    usersUpdated?: unknown[];
  }>;
  fetchId?: string;
};

export type AdConnectionUpdateResponse = AdConnection & {
  dataSource?: string;
  targetGroups?: string[];
  enabledServices?: string[];
};

export type SamlSettingsUpdatePayload = Omit<
  SamlSettingsState,
  "customMappings"
> & { customMappings?: Record<string, string> };

export type MicrosoftSettingsSavePayload = {
  tenants?: { tenantId?: string; hasTeamsAdminConsent?: boolean }[];
  groups?: string[];
  mappings?: MicrosoftMappings;
  customMappings?: Record<string, string>;
  emailPattern?: string;
  buttonText?: string;
};

export function asMicrosoftSettingsBody(
  body: MicrosoftSettingsSavePayload,
): OpenAPI.PUT<"/organisations/{id}/auth/microsoft">["requestBody"] {
  return body as OpenAPI.PUT<"/organisations/{id}/auth/microsoft">["requestBody"];
}

export function asSamlSettingsBody(
  body: SamlSettingsUpdatePayload,
): OpenAPI.PUT<"/organisations/{id}/auth/saml">["requestBody"] {
  return body as OpenAPI.PUT<"/organisations/{id}/auth/saml">["requestBody"];
}

export function asSamlSettingsState(data: object): SamlSettingsState {
  const saml = data as SamlSettingsState;
  return {
    entryPoint: saml.entryPoint || "",
    logoutUrl: saml.logoutUrl || "",
    cert: saml.cert || "",
    mappings: saml.mappings || {},
    authoriseAttribute: saml.authoriseAttribute || "",
    authorisePattern: saml.authorisePattern || "",
    buttonText: saml.buttonText || "",
    sp: saml.sp || {},
  };
}

export type SamlCustomMapping = {
  localKey: string;
  foreignKey: string;
  displayName?: string;
  id: string;
};

export type SamlSettingsState = {
  entryPoint: string;
  logoutUrl: string;
  cert: string;
  mappings: MicrosoftMappings;
  authoriseAttribute: string;
  authorisePattern: string;
  buttonText: string;
  sp: Record<string, string>;
  customMappings?: Record<string, string>;
};

export type OrganisationOnboardingData = {
  isEnabled?: boolean;
  screens?: OnboardingScreen[];
};

export type OnboardingScreen = {
  _id: string;
  content: string;
  createdAt?: string;
  updatedAt?: string;
  requireUserAgreement?: boolean;
  user?: string;
  createdBy?: OpenAPI.Schemas["users"];
};

export type OnboardingTemplate = {
  id: string;
  name: string;
  organisationType?: string;
  screens?: { content: string }[];
  createdBy?: string;
};

export type OnboardingToggleResponse = {
  isEnabled: boolean;
  onboardingCreated?: boolean;
  newOnboardingData?: OrganisationOnboardingData;
};

export type RulesGroupOption = { _id: string; name: string };

export type RulesRoleOption = Pick<OpenAPI.Schemas["Role"], "_id" | "name">;

export function exploreOptionsToQuery(
  options: AdminExploreSearchParams,
): OpenAPI.GET<"/organisations/{id}/explore/ideas">["parameters"]["query"] {
  return {
    page: options.page,
    limit: options.limit,
    challenges: options.challenges.length
      ? options.challenges.join(",")
      : undefined,
    departments: options.departments.length
      ? options.departments.join(",")
      : undefined,
    tags: options.tags.length ? options.tags.join(",") : undefined,
    status: options.status || undefined,
    name: options.name || undefined,
    progression: options.progression.length
      ? options.progression.join(",")
      : undefined,
    labels: options.labels.length ? options.labels.join(",") : undefined,
    creationDate: options.creationDate || undefined,
    fieldChoices: options.fieldChoices.length
      ? options.fieldChoices.join(",")
      : undefined,
    laneSearchName: options.laneSearchName || undefined,
    sort: options.sort || undefined,
    sortDirection: options.sortDirection,
  };
}
